import {
  WSActions,
  WSCard,
  WSElement,
  WSForm,
  WSInfoBox,
  WSList,
  WSSectionToolbar,
  WSSelect
} from "@wingspanhq/fe-component-library";
import {
  PayoutDestinationType,
  PayoutPreferences
} from "@wingspanhq/payments/dist/interfaces";
import { useWSMutation } from "@ws-react-query";
import { useMemo } from "react";
import * as Yup from "yup";
import { WSErrorMessage } from "../../../components/WSErrorMessage/WSErrorMessage";
import { QUERY_INTERNAL_ACCOUNTS } from "../../../query/banking/queries/useQueryInternalAccounts";
import { useUserId } from "../../../query/hooks/helpers";
import { QUERY_PAYOUT_SETTINGS } from "../../../query/payments/keys";
import { createInternalAccount } from "../../../services/api/banking/internalAccounts";
import { components } from "../../../services/api/banking/types";
import { paymentsService } from "../../../services/payments";
import { dictionaryInternalAccountCurrency } from "../../../shared/constants/currency";
import { getCurrencyOption } from "../../../shared/utils/currency";
import { WSServiceError } from "../../../utils/serviceHelper";

export type FormData = {
  currency: components["schemas"]["InternalAccountCreate"]["currency"] | null;
};

type Props = {
  onBack?: () => void;
  onContinue?: () => void;
};

export const FormAddPlatformBalance: React.FC<Props> = props => {
  const userId = useUserId();
  const availableCurrencies = useMemo(() => {
    return dictionaryInternalAccountCurrency["ApPayouts"] || [];
  }, []);

  const [submit, meta] = useWSMutation<
    components["schemas"]["InternalAccount"],
    WSServiceError,
    FormData
  >(
    async data => {
      if (!data.currency) {
        throw new Error("Currency is required");
      }

      const internalAccount = await createInternalAccount({
        type: "ApPayouts",
        currency: data.currency
      });

      await paymentsService.payoutSettings.update(userId, {
        payoutPreferences: PayoutPreferences.Standard,
        standard: {
          destinationId: internalAccount.internalAccountId,
          destinationType: "InternalAccount" as PayoutDestinationType
        }
      });

      return internalAccount;
    },
    {
      dependencies: [QUERY_INTERNAL_ACCOUNTS, QUERY_PAYOUT_SETTINGS],
      onSuccess: props.onContinue
    }
  );

  return (
    <WSForm<FormData>
      validationSchema={Yup.object().shape({
        currency: Yup.string().nullable().required("Required")
      })}
      onSubmit={submit}
    >
      <WSList gap="2XL">
        <WSCard>
          <WSList gap="2XL">
            <WSElement>
              <WSSectionToolbar title="Introducing Platform Balance" />

              <WSInfoBox title="Platform balance">
                <p>
                  You have a Platform Balance available to you to hold your
                  earnings securely in a preferred currency of your choice until
                  you are ready to transfer it out to the bank account you’ve
                  added. This is a benefit available to you for free with the
                  Wingspan platform.
                </p>

                <p>
                  <b>Why is this offered?</b> Some non-U.S. contractors prefer
                  to keep their earnings on the platform in a preferred currency
                  until they are ready to transfer the balance to their external
                  bank account for various reasons (such as exchange rates and
                  inflation). Additionally, in the event that a payout to the
                  external bank account fails repeatedly, the earnings will be
                  held in the Platform Balance - allowing contractors to
                  transfer it out once any issues are resolved.
                </p>
              </WSInfoBox>
            </WSElement>

            <WSForm.Field
              name="currency"
              component={WSSelect}
              componentProps={{
                label: "Platform Balance currency",
                helperText:
                  "Please select a supported currency. Once set, this cannot be changed.",
                options: availableCurrencies.map(getCurrencyOption)
              }}
            />

            <WSErrorMessage contextKey="InternalAccount" error={meta.error} />
          </WSList>
        </WSCard>

        <WSActions
          alignment="fill"
          buttons={[
            {
              label: "Back",
              kind: "Secondary",
              onClick: props.onBack,
              visible: !!props.onBack,
              type: "button"
            },
            {
              label: "Continue",
              loading: meta.isLoading
            }
          ]}
        />
      </WSList>
    </WSForm>
  );
};
